import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  loadICSCalendar,
  type CalendarEvent,
  eventOccursOnCalendarDay,
  isMultiDayCalendarEvent,
  formatEventTimeRange,
  eventOverlapsLocalDay,
  eventDisplayStartParts,
} from '../lib/icsCalendar'
import { grantCalendarReadPermission } from '../lib/calendarSync'
import { getSetting } from '../lib/apiPhpAdapter'
import './CalendarView.css'

function CalendarView() {
  const navigate = useNavigate()
  useAuth() // Для совместимости, но не используем authenticated
  const [currentDate, setCurrentDate] = useState(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [showDayModal, setShowDayModal] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [_syncResult, setSyncResult] = useState<{
    success: number
    skipped: number
    failed: number
    errors: string[]
  } | null>(null)
  
  // URL .ics файла календаря - по умолчанию календарь школы, но можно настроить через настройки
  const [icsUrl, setIcsUrl] = useState('https://outlook.office365.com/owa/calendar/b86242d5bf2d4496a89c04f9eaf4cc69@iss-cheb.cz/6d0809beabcb451898dddfc17196b3433773793055790110233/calendar.ics')
  
  // Загружаем настройку календаря из базы данных при загрузке компонента
  useEffect(() => {
    const loadCalendarSetting = async () => {
      try {
        // Проверяем, есть ли настройка календаря для интранета
        // Если нет, используем календарь школы по умолчанию
        const calendarUrl = await getSetting('calendar_url')
        if (calendarUrl) {
          setIcsUrl(calendarUrl)
        }
      } catch (error) {
        console.error('Error loading calendar setting:', error)
        // Используем календарь школы по умолчанию
      }
    }
    
    loadCalendarSetting()
  }, [])

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Загружаем события календаря из .ics файла
  useEffect(() => {
    if (icsUrl) {
      loadEvents()
    }
    
    // Проверяем, подписан ли пользователь на календарь
    const subscription = localStorage.getItem('calendar_subscription')
    setIsSubscribed(subscription === 'true')
  }, [currentDate, icsUrl])

  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Создаем начало и конец месяца с учетом времени
      const startDate = new Date(year, month, 1, 0, 0, 0, 0)
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999)
      
      const calendarEvents = await loadICSCalendar(icsUrl, startDate, endDate)
      setEvents(calendarEvents)
    } catch (err: any) {
      console.error('Error loading calendar events:', err)
      setError(err.message || 'Nepodařilo se načíst události z kalendáře')
    } finally {
      setLoading(false)
    }
  }

  // Навигация по месяцам
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Получаем первый день месяца и количество дней
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  
  // Корректируем день недели (0 = воскресенье -> 6, 1 = понедельник -> 0)
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

  const isMultiDayEvent = (event: CalendarEvent) => isMultiDayCalendarEvent(event)

  const getEventsForDay = (day: number) =>
    events.filter((event) => eventOccursOnCalendarDay(event, year, month, day))

  // Проверяем, является ли день сегодняшним
  const isToday = (day: number) => {
    const today = new Date()
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    )
  }

  const monthNames = [
    'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
    'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
  ]

  const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']

  // Получаем сегодняшнюю дату
  const today = new Date()
  const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year
  const todayDay = today.getDate()

  const todayStart = new Date(today.getFullYear(), today.getMonth(), todayDay, 0, 0, 0, 0)
  const todayEnd = new Date(today.getFullYear(), today.getMonth(), todayDay, 23, 59, 59, 999)
  
  const todayEvents = events.filter((event) => eventOverlapsLocalDay(event, todayStart, todayEnd))

  const pastEvents = events.filter((event) => {
    const eventEnd = new Date(event.end.dateTime)
    return eventEnd.getTime() <= todayStart.getTime() && !todayEvents.includes(event)
  })

  const upcomingEvents = events.filter((event) => {
    const eventStart = new Date(event.start.dateTime)
    return eventStart.getTime() > todayEnd.getTime() && !todayEvents.includes(event)
  })

  const sortedTodayEvents = [...todayEvents].sort((a, b) => {
    return new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime()
  })

  const sortedPastEvents = [...pastEvents].sort((a, b) => {
    return new Date(b.start.dateTime).getTime() - new Date(a.start.dateTime).getTime()
  })

  const sortedUpcomingEvents = [...upcomingEvents].sort((a, b) => {
    return new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime()
  })

  const getSelectedDayEvents = () => {
    if (selectedDay === null) return []
    return events
      .filter((event) => eventOccursOnCalendarDay(event, year, month, selectedDay))
      .sort((a, b) => new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime())
  }

  const handleDayClick = (day: number) => {
    setSelectedDay(day)
    setShowDayModal(true)
  }

  const handleSyncCalendar = async () => {
    if (!confirm('Chcete přidat školní kalendář do vašeho Outlook? Otevře se odkaz na kalendář a Outlook automaticky navrhne jeho přidání. Kalendář bude pouze pro čtení - nebudete moci měnit události.')) {
      return
    }

    setSyncing(true)
    setSyncResult(null)

    try {
      // Открываем ссылку на .ics файл
      const result = await grantCalendarReadPermission()
      
      if (result.success) {
        setIsSubscribed(true)
        alert(result.message)
      } else {
        alert(result.message)
      }
    } catch (err: any) {
      console.error('Error adding calendar:', err)
      alert(`Chyba při přidávání kalendáře: ${err.message}`)
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button 
            onClick={() => navigate(-1)} 
            className="back-button"
            style={{
              background: '#14b8a6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#0d9488'
              e.currentTarget.style.transform = 'translateY(-1px)'
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#14b8a6'
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            ← Zpět
          </button>
          <h1>📆 Kalendář akcí školy</h1>
        </div>
        <button 
          onClick={handleSyncCalendar} 
          className="sync-button"
          disabled={syncing}
        >
          {syncing ? '⏳ Přidávání...' : isSubscribed ? '✅ Kalendář přidán do Outlook' : '📥 Přidat kalendář do Outlook'}
        </button>
        
        {isSubscribed && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem', 
            backgroundColor: '#e8f5e9', 
            borderRadius: '8px',
            fontSize: '0.9rem',
            maxWidth: '600px'
          }}>
            <strong>✅ Kalendář školy je přidán</strong>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
              Otevřete Outlook a zobrazte kalendář školy v seznamu kalendářů. 
              Události se budou automaticky zobrazovat v Outlook. Kalendář je pouze pro čtení - 
              nemůžete měnit události v kalendáři školy.
            </p>
            <button 
              onClick={() => {
                localStorage.removeItem('calendar_subscription')
                localStorage.removeItem('calendar_subscription_date')
                setIsSubscribed(false)
                alert('Informace o přidání kalendáře byla odstraněna. Kalendář zůstává v Outlook - pro jeho odstranění použijte Outlook.')
              }}
              style={{
                marginTop: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Odstranit informaci o přidání
            </button>
          </div>
        )}
      </div>

      <div className="calendar-content">
        <div className="calendar-section">
          <div className="calendar-controls">
            <button onClick={goToPreviousMonth} className="nav-button">‹</button>
            <h2>{monthNames[month]} {year}</h2>
            <button onClick={goToNextMonth} className="nav-button">›</button>
            <button onClick={goToToday} className="today-button">Dnes</button>
          </div>

          {loading && (
            <div className="calendar-loading">Načítání událostí...</div>
          )}

      {error && (
        <div className="calendar-error">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <h3>Chyba při načítání kalendáře</h3>
            <p>{error}</p>
            {error.includes('oprávnění') && (
              <div className="error-instructions">
                <p><strong>Jak opravit:</strong></p>
                <ol>
                  <li>Otevřete <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer">Azure Portal</a></li>
                  <li>Přejděte na <strong>Azure Active Directory</strong> → <strong>App registrations</strong></li>
                  <li>Najděte vaše aplikace → <strong>API permissions</strong></li>
                  <li>Přidejte oprávnění:
                    <ul>
                      <li><strong>Calendars.Read</strong></li>
                      <li><strong>Calendars.Read.Shared</strong></li>
                    </ul>
                  </li>
                  <li><strong>DŮLEŽITÉ:</strong> Klikněte na <strong>"Grant admin consent"</strong></li>
                  <li>Odhlásíte se a přihlaste znovu</li>
                </ol>
              </div>
            )}
            <button onClick={loadEvents} className="retry-button">Zkusit znovu</button>
          </div>
        </div>
      )}

          {!loading && !error && (
            <div className="calendar-grid">
              {/* Заголовки дней недели */}
              {dayNames.map(day => (
                <div key={day} className="calendar-day-header">
                  {day}
                </div>
              ))}

              {/* Пустые ячейки до первого дня месяца */}
              {Array.from({ length: adjustedFirstDay }).map((_, index) => (
                <div key={`empty-${index}`} className="calendar-day empty"></div>
              ))}

              {/* Дни месяца */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1
                const dayEvents = getEventsForDay(day)
                const isTodayDay = isToday(day)
                const dayDate = new Date(year, month, day)
                const isPast = dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())
                const hasPastEvents = dayEvents.some(e => new Date(e.start.dateTime) < new Date())
                const hasUpcomingEvents = dayEvents.some(e => new Date(e.start.dateTime) >= new Date())
                const hasMultiDayEvents = dayEvents.some(e => isMultiDayEvent(e))

                return (
                  <div
                    key={day}
                    className={`calendar-day ${isTodayDay ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''} ${isPast ? 'past-day' : ''} ${hasPastEvents ? 'has-past-events' : ''} ${hasUpcomingEvents ? 'has-upcoming-events' : ''} ${hasMultiDayEvents ? 'has-multiday-events' : ''}`}
                    onClick={() => handleDayClick(day)}
                    style={{ cursor: 'pointer' }}
                    title={`${day}. ${monthNames[month]} - ${dayEvents.length} událostí`}
                  >
                    <div className="day-number">{day}</div>
                    {dayEvents.length > 0 && (
                      <div className={`day-events-count ${hasMultiDayEvents ? 'multiday-count' : ''}`}>{dayEvents.length}</div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="events-section">
          <h3>Události</h3>
          {loading && (
            <div className="events-loading">Načítání...</div>
          )}
          {error && (
            <div className="events-error">{error}</div>
          )}
          {!loading && !error && (
            <div className="events-list">
              {/* События сегодня */}
              {isCurrentMonth && sortedTodayEvents.length > 0 && (
                <div className="events-group">
                  <h4 className="events-group-title today-title">📅 Dnes ({sortedTodayEvents.length})</h4>
                  {sortedTodayEvents.map(event => {
                    const parts = eventDisplayStartParts(event)
                    const multiDay = isMultiDayEvent(event)

                    return (
                      <div key={event.id} className={`event-card today-event ${multiDay ? 'multiday-event' : ''}`}>
                        <div className="event-date today-date">
                          <div className="event-day">{parts.day}</div>
                          <div className="event-month">{monthNames[parts.month0].substring(0, 3)}</div>
                        </div>
                        <div className="event-details">
                          <div className="event-time-range">
                            {formatEventTimeRange(event)}
                          </div>
                          <div className="event-title">{event.subject}</div>
                          {event.location?.displayName && (
                            <div className="event-location">📍 {event.location.displayName}</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Предстоящие события */}
              {sortedUpcomingEvents.length > 0 && (
                <div className="events-group">
                  <h4 className="events-group-title upcoming-title">🔜 Nadcházející ({sortedUpcomingEvents.length})</h4>
                  {sortedUpcomingEvents.map(event => {
                    const parts = eventDisplayStartParts(event)
                    const multiDay = isMultiDayEvent(event)

                    return (
                      <div key={event.id} className={`event-card upcoming-event ${multiDay ? 'multiday-event' : ''}`}>
                        <div className="event-date">
                          <div className="event-day">{parts.day}</div>
                          <div className="event-month">{monthNames[parts.month0].substring(0, 3)}</div>
                        </div>
                        <div className="event-details">
                          <div className="event-time-range">
                            {formatEventTimeRange(event)}
                          </div>
                          <div className="event-title">{event.subject}</div>
                          {event.location?.displayName && (
                            <div className="event-location">📍 {event.location.displayName}</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Прошедшие события */}
              {sortedPastEvents.length > 0 && (
                <div className="events-group">
                  <h4 className="events-group-title past-title">📜 Minulé ({sortedPastEvents.length})</h4>
                  {sortedPastEvents.map(event => {
                    const parts = eventDisplayStartParts(event)
                    const multiDay = isMultiDayEvent(event)

                    return (
                      <div key={event.id} className={`event-card past-event ${multiDay ? 'multiday-event' : ''}`}>
                        <div className="event-date">
                          <div className="event-day">{parts.day}</div>
                          <div className="event-month">{monthNames[parts.month0].substring(0, 3)}</div>
                        </div>
                        <div className="event-details">
                          <div className="event-time-range">
                            {formatEventTimeRange(event)}
                          </div>
                          <div className="event-title">{event.subject}</div>
                          {event.location?.displayName && (
                            <div className="event-location">📍 {event.location.displayName}</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {sortedTodayEvents.length === 0 && sortedUpcomingEvents.length === 0 && sortedPastEvents.length === 0 && (
                <div className="no-events">Žádné události v tomto měsíci</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно для просмотра событий выбранного дня */}
      {showDayModal && selectedDay !== null && (
        <div className="day-modal-overlay" onClick={() => setShowDayModal(false)}>
          <div className="day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="day-modal-header">
              <h3>
                {selectedDay}. {monthNames[month]} {year}
              </h3>
              <button className="day-modal-close" onClick={() => setShowDayModal(false)}>×</button>
            </div>
            <div className="day-modal-content">
              {getSelectedDayEvents().length === 0 ? (
                <div className="no-events">Žádné události v tento den</div>
              ) : (
                getSelectedDayEvents().map(event => {
                  const parts = eventDisplayStartParts(event)
                  const isPast = new Date(event.end.dateTime).getTime() <= Date.now()
                  const multiDay = isMultiDayEvent(event)

                  return (
                    <div key={event.id} className={`event-card ${isPast ? 'past-event' : 'upcoming-event'} ${multiDay ? 'multiday-event' : ''}`}>
                      <div className="event-date">
                        <div className="event-day">{parts.day}</div>
                        <div className="event-month">{monthNames[parts.month0].substring(0, 3)}</div>
                      </div>
                      <div className="event-details">
                        <div className="event-time-range">
                          {formatEventTimeRange(event)}
                        </div>
                        <div className="event-title">{event.subject}</div>
                        {event.location?.displayName && (
                          <div className="event-location">📍 {event.location.displayName}</div>
                        )}
                        {event.body?.content && (
                          <div className="event-description">{event.body.content}</div>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CalendarView
